import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Grid, CircularProgress } from "@mui/material";
// Custom MUI-based components (assumed converted already)
import FormContainer from "../components/FormContainer";
import FloatingLabelInput from "../components/FloatingLabelInput";
import Button from "../components/Button";
import TextArea from "../components/TextArea";
import CitySearchInput from "../components/CitySearchInput";
import DateInput from "../components/DateInput";
import Dropdown from "../components/Dropdown";
import SearchSection from "../components/SearchSection";
import ReasonSearchDropdown from "../components/ReasonSearchDropdown";
import AddNewReasonModal from "../components/AddNewReasonModal";

// Import icons for GST validation feedback.
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// Helper function to convert an ISO date string to "YYYY-MM-DD" format.
const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
};

// New helper to convert a date string from "DD-MM-YYYY" to "YYYY-MM-DD"
const convertDMYtoYMD = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

function InquiryForm_v1({ loggedInUser, onClose, inquiryData: propInquiryData, connectionString }) {
    const path = connectionString;

    const navigate = useNavigate();
    const location = useLocation();
    const [formErrors, setFormErrors] = useState([]);

    // Submission state for the form.
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- STATES FOR GST LOOKUP ---
    const [gstLoading, setGstLoading] = useState(false);
    const [gstError, setGstError] = useState("");
    const [gstValid, setGstValid] = useState(null); // Assume valid by default

    const inquiryData = propInquiryData || location.state?.inquiryData;
    const overallMode = onClose ? "followup" : (location.state?.mode || "insert");
    const [displayMode] = useState(overallMode);
    const [clientMode, setClientMode] = useState("existClient");

    // ---------------------- Inquiry Form State Variables ----------------------
    const [inquiryId, setInquiryId] = useState("");
    const [inquiryNumber, setInquiryNumber] = useState("");
    const [InqDate, setInqDate] = useState("");
    const [leadsourceID, setLeadsourceID] = useState("");
    const [leadSourceValue, setLeadSourceValue] = useState("");
    const [userId, setUserId] = useState("");
    const [employeeName, setEmployeeName] = useState("");
    const [leadStatusID, setLeadStatusID] = useState("");
    const [leadStatusValue, setLeadStatusValue] = useState("");
    const [priorityID, setPriorityID] = useState("");
    const [priorityValue, setPriorityValue] = useState("");
    const [mobile, setMobile] = useState("");
    const [clientName, setClientName] = useState("");
    const [gstInNumber, setGstInNumber] = useState("");
    const [contactPerson, setContactPerson] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [stateField, setStateField] = useState("");
    const [stateCode, setStateCode] = useState("");
    // We'll use requirement & remarks to capture inquiry specifics.
    const [requirement, setRequirement] = useState("");
    const [remarks, setRemarks] = useState("");
    const [qty, setQty] = useState("1");
    const [rate, setRate] = useState("");
    const [amount, setAmount] = useState("");
    const [nextFollowUpDate, setNextFollowUpDate] = useState("");
    const [pincode, setPincode] = useState("");
    const [clientId, setClientId] = useState("");

    // ---------------------- Additional Flags ----------------------
    const [followupDateManuallySet, setFollowupDateManuallySet] = useState(false);

    // ---------------------- Dropdown Options ----------------------
    const [sourceOptions, setSourceOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [usersOptions, setUsersOptions] = useState([]);
    const [priorityOptions, setPriorityOptions] = useState([]);
    const [cities, setCities] = useState([]);

    // ---------------------- Followup Specific (if any) ----------------------
    const [reasonOptions, setReasonOptions] = useState([]);
    const [selectedReason, setSelectedReason] = useState(null);
    const [showAddNewReasonModal, setShowAddNewReasonModal] = useState(false);

    // ---------------------- SearchSection States ----------------------
    const [normalDropdown, setNormalDropdown] = useState("");
    const [searchText, setSearchText] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);

    // ---------------------- Additional State: isFailed ----------------------
    const [isFailed, setIsFailed] = useState(false);

    // ------------------------- Date Helpers -------------------------
    const getTodayDate = () => new Date().toISOString().split("T")[0];
    const getFutureDate = () => {
        const dt = new Date();
        dt.setDate(dt.getDate() + 7);
        return dt.toISOString().split("T")[0];
    };

    // ---------------------- Rebind Selected Record ----------------------
    useEffect(() => {
        if (!inquiryData) {
            setInqDate(getTodayDate());
            setNextFollowUpDate(getFutureDate());
            return;
        }
        const data = inquiryData?.inquiry || inquiryData;
        setInquiryId(data.inquiryId || "");
        setClientId(data.clientId || data.id || "");
        setClientName(data.clientname || "");
        setMobile(data.mobileNo || data.mobile || "");
        setContactPerson(data.contactPerson || data.clientContact || "");
        setEmail(data.emailId || data.email || "");
        setGstInNumber(data.gstno || "");
        setAddress(data.address || "");
        setCity(data.city || "");
        setStateField(data.state || "");
        setStateCode(data.stateCode || "");
        setInquiryNumber(data.inquiryNumber || "");
        setInqDate(data.inquiryDate ? formatDateForInput(data.inquiryDate) : "");

        setLeadStatusID(data.statusId ? String(data.statusId) : "");
        setLeadStatusValue(data.statusName || "");
        setPriorityID(data.priorityid ? String(data.priorityid) : "");
        setPriorityValue(data.priority || "");
        setLeadsourceID(data.LastleadSourceId ? String(data.LastleadSourceId) : "");
        setLeadSourceValue(data.LastleadSourceName || "");
        setRequirement(data.requirement || "");
        setRemarks(data.Lastremark || "");

        if (data.followUpDate) {
            setNextFollowUpDate(convertDMYtoYMD(data.followUpDate));
        } else if (data.nextFollowUpDate) {
            setNextFollowUpDate(formatDateForInput(data.nextFollowUpDate));
        }

        setIsFailed(data.statusName && data.statusName.toLowerCase() === "failed");
        if (data.qty) setQty(data.qty);
        if (data.rate) setRate(data.rate);
        if (data.amount) setAmount(data.amount);
    }, [inquiryData]);

    // ---------------------- Preselect "Assign" Dropdown from loggedInUser ----------------------
    useEffect(() => {
        if (loggedInUser) {
            if (overallMode === "insert") {
                setUserId(loggedInUser.userId);
                setEmployeeName(loggedInUser.employeeName);
            } else {
                if (inquiryData) {
                    const data = inquiryData.inquiry || inquiryData;
                    if (data.userId && data.employeeName) {
                        setUserId(data.userId);
                        setEmployeeName(data.employeeName);
                    } else {
                        setUserId(loggedInUser.userId);
                        setEmployeeName(loggedInUser.employeeName);
                    }
                } else {
                    setUserId(loggedInUser.userId);
                    setEmployeeName(loggedInUser.employeeName);
                }
            }
        }
    }, [loggedInUser, overallMode, inquiryData]);

    useEffect(() => {
        if (userId && usersOptions.length > 0) {
            const found = usersOptions.some((user) => String(user.userId) === String(userId));
            if (!found) {
                setUsersOptions((prev) => [...prev, { userId, employeeName }]);
            }
        }
    }, [userId, employeeName, usersOptions]);

    // ---------------------- Set Default Priority and Follow-Up Date in Insert Mode ----------------------
    useEffect(() => {
        if (overallMode === "insert") {
            setPriorityID("3");
            setPriorityValue("High");
            setLeadStatusID("1");
            setLeadStatusValue("Pipeline");
            setLeadsourceID("5");
            setLeadSourceValue("Cold Call");
            if (InqDate && !followupDateManuallySet) {
                const inqDateObj = new Date(InqDate);
                const sevenDaysLater = new Date(inqDateObj);
                sevenDaysLater.setDate(inqDateObj.getDate() + 7);
                setNextFollowUpDate(sevenDaysLater.toISOString().split("T")[0]);
            }
        }
    }, [InqDate, overallMode, followupDateManuallySet]);

    // ---------------------- Fetch Inquiry Number on Mount (only in insert mode) ----------------------
    useEffect(() => {
        if (overallMode === "insert") {
            const fetchInquiryNumber = async () => {
                try {
                    const response = await axios.get(`${path}/api/getInqNumber`);
                    setInquiryNumber(response.data.inquiryNumber);
                } catch (error) {
                    console.error("Error fetching InquiryNumber:", error);
                }
            };
            fetchInquiryNumber();
        }
    }, [overallMode]);

    // ---------------------- Fetch Dropdown Options on Mount ----------------------
    useEffect(() => {
        axios.get(`${path}/api/sources`).then((res) => setSourceOptions(res.data.data || [])).catch((error) => console.error("Error fetching sources:", error));
        axios.get(`${path}/api/status`).then((res) => setStatusOptions(res.data.data || [])).catch((error) => console.error("Error fetching status:", error));
        axios.get(`${path}/api/users`, { params: { companyId: loggedInUser.companyId } }).then((res) => setUsersOptions(Array.isArray(res.data.data) ? res.data.data : [])).catch((error) => console.error("Error fetching users:", error));
        axios.get(`${path}/api/priority`).then((res) => { if (res.data.success) setPriorityOptions(res.data.data); }).catch((error) => console.error("Error fetching priorities:", error));
        axios.get(`${path}/api/get-cities`).then((res) => setCities(res.data.cities || [])).catch((error) => console.error("Error fetching cities:", error));
    }, []);

    // ---------------------- Fetch Reason Options for Followup Mode ----------------------
    useEffect(() => {
        if (overallMode === "followup") {
            axios.get(`${path}/api/reasons`, { params: { search: "" } }).then((res) => setReasonOptions(res.data.data || [])).catch((error) => console.error("Error fetching reason options:", error));
        }
    }, [overallMode]);

    // ---------------------- Calculate Amount on Qty or Rate Change ----------------------
    useEffect(() => {
        const q = parseFloat(qty) || 0;
        const r = parseFloat(rate) || 0;
        setAmount(String((q * r).toFixed(2)));
    }, [qty, rate]);

    // ---------------------- Dropdown Change Handlers ----------------------
    const handleSourceChange = (selected) => {
        setLeadsourceID(selected.LeadsourceID);
        setLeadSourceValue(selected.LeadSource);
    };

    const handleAssignChange = (selected) => {
        setUserId(selected.userId);
        setEmployeeName(selected.employeeName);
    };

    const handleStatusChange = (selected) => {
        setLeadStatusID(selected.LeadStatusID);
        setLeadStatusValue(selected.LeadStatus);
        setIsFailed(selected.LeadStatus && selected.LeadStatus.toLowerCase() === "failed");
    };

    const handlePriorityChange = (selected) => {
        setPriorityID(selected.id);
        setPriorityValue(selected.value);
    };

    // ---------------------- Search Section Functions ----------------------
    const handleSearchByChange = (selectedValue) => {
        setNormalDropdown(selectedValue);
    };

    const Fn_HandleNewToggle = (e) => {
        const checked = e.target.checked;
        setIsNew(checked);
        if (checked) {
            setClientMode("newClient");
            setIsDisabled(false);
        } else {
            setClientMode("existClient");
            setIsDisabled(true);
        }
    };

    const Fn_HandleSearch = async (searchValue) => {
        setSearchText(searchValue);
        if (searchValue.trim() === "") {
            setSearchResults([]);
            setShowResults(false);
            return;
        }
        if (!normalDropdown || normalDropdown.trim() === "") {
            setSearchResults([]);
            setShowResults(false);
            return;
        }
        try {
            const field = normalDropdown;
            const res = await axios.get(`${path}/api/company-search`, { params: { field, search: searchValue } });
            if (res.status === 403 || res.data.status === 403) {
                alert("Select a value from the dropdown!");
                setSearchResults([]);
                setShowResults(false);
                return;
            }
            const results = res.data.data || [];
            setSearchResults(results);
            setShowResults(true);
            if (results.length === 0) {
                setIsNew(true);
                setClientMode("newClient");
                setIsDisabled(false);
            } else {
                setIsNew(false);
                setClientMode("existClient");
                setIsDisabled(true);
            }
        } catch (error) {
            console.error("Error performing search:", error);
        }
    };

    const Fn_HandleSelectedResult = (result) => {
        setClientId(result.id);
        setClientName(result.clientname || "");
        setMobile(result.mobileNo || "");
        setContactPerson(result.contactPerson || "");
        setEmail(result.emailId || "");
        setGstInNumber(result.gstno || "");
        setAddress(result.address || "");
        setCity(result.city || "");
        setStateField(result.state || "");
        setStateCode(result.stateCode || "");
        setPincode(result.pinCode ? String(result.pinCode) : "");
        setClientMode("existClient");
        setIsDisabled(true);
        setIsNew(false);
        setShowResults(false);
        setSearchText("");
    };

    // ---------------------- New: GST Input OnBlur Handler ----------------------
    const handleGstBlur = async (e) => {
        const gstValue = e.target.value;
        // If GST field is empty, clear client details and mark GST as invalid.
        if (!gstValue) {
            setClientName("");
            setContactPerson("");
            setAddress("");
            setCity("");
            setStateField("");
            setStateCode("");
            setGstValid(false);
            return;
        }
        setGstError(""); // Clear previous error
        setGstLoading(true); // Start loader
        try {
            // Call your backend GST lookup endpoint.
            const response = await axios.get(`${path}/api/gst-lookup`, { params: { searchGstin: gstValue } });
            setGstValid(true);
            // Update client fields based on the response.
            setClientName(response.data.data.clientName || "");
            setContactPerson(response.data.data.contactPerson || "");
            setAddress(response.data.data.address || "");
            setCity(response.data.data.city || "");
            setStateField(response.data.data.state || "");
            setStateCode(response.data.data.stateCode || "");
        } catch (error) {
            setGstValid(false);
            setGstError(error.response?.data?.message || "Unable to fetch details for given GST No.");
            // Clear client details if GST lookup fails.
            setClientName("");
            setContactPerson("");
            setAddress("");
            setCity("");
            setStateField("");
            setStateCode("");
        }
        setGstLoading(false);
    };

    // ---------------------- Prevent Form Submission on Enter ----------------------
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter") e.preventDefault();
    }, []);

    // ---------------------- Clear All Form Fields ----------------------
    const clearFormFields = () => {
        setInquiryId("");
        setInquiryNumber("");
        setInqDate("");
        setLeadsourceID("");
        setLeadSourceValue("");
        setUserId("");
        setEmployeeName("");
        setLeadStatusID("");
        setLeadStatusValue("");
        setPriorityID("");
        setPriorityValue("");
        setMobile("");
        setClientName("");
        setGstInNumber("");
        setContactPerson("");
        setEmail("");
        setAddress("");
        setCity("");
        setStateField("");
        setStateCode("");
        setRequirement("");
        setRemarks("");
        setQty("");
        setRate("");
        setAmount("");
        setNextFollowUpDate("");
        setPincode("");
        setClientId("");
    };

    // ---------------------- Handle Close ----------------------
    const handleClose = useCallback(() => {
        if (overallMode === "followup") {
            if (typeof onClose === "function") {
                onClose();
            }
        } else {
            navigate(-1);
        }
    }, [navigate, overallMode, onClose]);

    // ---------------------- Form Submission Handler ----------------------
    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors([]);
            const errors = [];

            if (!clientName.trim()) {
                errors.push("Client Name is mandatory");
            }
            if (!mobile.trim()) {
                errors.push("Mobile is mandatory");
            } else if (mobile.trim().length !== 10) {
                errors.push("Mobile No must be exactly 10 digits");
            }
            if (!contactPerson.trim()) {
                errors.push("Contact Person is mandatory");
            }
            if (gstInNumber && gstInNumber.trim() !== "" && gstInNumber.trim().length !== 15) {
                errors.push("GST No must be exactly 15 characters");
            }

            // New validation: Follow-up date must not be earlier than Inquiry Date.
            if (new Date(nextFollowUpDate) < new Date(InqDate)) {
                errors.push("Follow-up date cannot be earlier than Inquiry date.");
            }

            if (errors.length > 0) {
                setFormErrors(errors);
                return;
            }

            setIsSubmitting(true);

            const submissionData = { InqDate, inquiryNumber, leadsourceID, leadSourceValue, userId, employeeName, leadStatusID, leadStatusValue, priorityID, priorityValue, mobile, clientName, gstInNumber, contactPerson, email, address, city, state: stateField, stateCode, requirement, remarks, nextFollowUpDate, pincode, clientId, companyId: loggedInUser.companyId, };

            try {
                if (overallMode === "edit") {
                    const response = await axios.put(`${path}/api/inquiryupdate/${clientId}`, submissionData);
                    if (response.status === 200 && response.data.success) {
                        alert("Inquiry updated successfully!");
                        navigate(-1);
                    } else {
                        alert("Update failed!");
                    }
                } else if (overallMode === "followup") {
                    // For follow-up mode, adjust the submission data if needed.
                    const followupData = { inqid: inquiryId, inquiryNumber, nextFollowUpDate, leadSourceId: parseInt(leadsourceID) || 0, leadSourceName: leadSourceValue, activityId: 0, activityName: "", statusId: parseInt(leadStatusID) || 0, statusName: leadStatusValue, companyId: 1, addUserId: parseInt(userId) || 0, employeeName, requirement, remark: remarks, qty: parseFloat(qty) || 0, rate: parseFloat(rate) || 0, amount: parseFloat(amount) || 0, lastpriorityid: parseInt(priorityID) || 0, lastpriority: priorityValue, };
                    const followupResponse = await axios.post(`${path}/api/inquiryfollowup`, followupData);
                    if (followupResponse.status === 201) {
                        alert("Followup submitted successfully!");
                    } else {
                        alert("Followup submission failed!");
                    }
                } else {
                    let id = clientId;
                    if (clientMode === "newClient" || clientMode === "new") {
                        const clientResponse = await axios.post(`${path}/api/insertClient`, { clientname: clientName, contactPerson, mobileNo: mobile, emailId: email, address, pinCode: pincode, city, state: stateField, stateCode, gstno: gstInNumber, });
                        if (clientResponse.status === 201 && clientResponse.data.success) {
                            id = clientResponse.data.id;
                            setClientId(id);
                        } else {
                            alert("Failed to insert client data!");
                            setIsSubmitting(false);
                            return;
                        }
                    }
                    if (!id) {
                        alert("Client ID is missing!");
                        setIsSubmitting(false);
                        return;
                    }
                    const enquiryResponse = await axios.post(`${path}/api/enquiry`, { ...submissionData, clientId: id, leadSourceName: leadSourceValue });
                    if (enquiryResponse.status === 201) {
                        alert("Enquiry submitted successfully!");
                        clearFormFields();
                        navigate(-1);
                    } else {
                        alert("Submission failed!");
                    }
                }
            } catch (error) {
                console.error("Error during submission:", error);
                alert("Something went wrong...!");
            } finally {
                setIsSubmitting(false);
            }
        },
        [inquiryId, InqDate, inquiryNumber, leadsourceID, leadSourceValue, userId, employeeName, leadStatusID, leadStatusValue, priorityID, priorityValue, mobile, clientName, gstInNumber, contactPerson, email, address, city, stateField, stateCode, requirement, remarks, nextFollowUpDate, pincode, clientId, overallMode, clientMode, navigate, loggedInUser.companyId, qty, rate, amount,]
    );

    const searchOptions = [
        { value: "", label: "-- Select --" },
        { value: "companyName", label: "Company Name" },
        { value: "mobileNumber", label: "Mobile No" },
        { value: "contactPerson", label: "Contact Person" },
        { value: "gstInNumber", label: "GST No" },
        { value: "emailId", label: "Email Id" },
    ];

    const disableLeftSection = overallMode === "followup" ? true : overallMode === "edit" ? false : isDisabled;
    const inquiryLabel = displayMode.toLowerCase() === "edit" ? "Edit Inquiry" : displayMode.toLowerCase() === "followup" ? "Inquiry Follow-Up" : "Add new Inquiry";

    return (
        <FormContainer>
            <Box onKeyDown={handleKeyDown} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 1, borderRadius: 1 }}>
                <Typography variant="h4" align="center" sx={{ fontWeight: "bold", mb: 4, mt: 2, textDecoration: "underline", textUnderlineOffset: 8 }}>
                    Inquiry Form -{" "} <Typography component="span" variant="subtitle2" sx={{ mt: -1, color: "error.main", fontWeight: "semibold" }}> {inquiryLabel} </Typography>
                </Typography>

                {/* Top Row: Inquiry No, Date, Source, Assign */}
                <Grid container spacing={1} sx={{ bgcolor: "grey.50", p: 1, border: "1px solid", borderColor: "grey.300", borderRadius: 1, fontSize: "0.875rem", fontWeight: 600, width: "95%", mb: 0, ml: 0, gap: 4 }}>
                    <Grid item xs={12} md={2}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0 }}>
                            <FloatingLabelInput label="Inq No." name="inquiryNumber" type="text" value={inquiryNumber} disabled fullWidth />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Box sx={{ display: "flex", alignItems: "center", mt: 0 }}>
                            {overallMode === "insert" ? (
                                <DateInput label="Inq. Date" allowToday value={InqDate} onChange={(e) => setInqDate(e.target.value)} onTodaySelect={(todayValue) => setInqDate(todayValue)} disabled={false} fullWidth />
                            ) : (
                                <DateInput label="Inq. Date" value={formatDateForInput(InqDate)} disabled={false} onChange={(e) => setInqDate(e.target.value)} fullWidth />
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0 }}>
                            <Dropdown id="source" name="source" label="Source" valueKey="LeadsourceID" labelKey="LeadSource" value={leadsourceID} options={sourceOptions} onChange={handleSourceChange} disabled={false} fullWidth />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0 }}>
                            <Dropdown id="assign" name="assign" label="Assign" valueKey="userId" labelKey="employeeName" value={userId} options={usersOptions} onChange={handleAssignChange} disabled={false} fullWidth />
                        </Box>
                    </Grid>
                </Grid>

                {overallMode === "insert" && (<SearchSection searchOptions={searchOptions} normalDropdownValue={normalDropdown} onSearchByChange={handleSearchByChange} searchText={searchText} onSearchTextChange={Fn_HandleSearch} showResults={showResults} searchResults={searchResults} onSelectResult={Fn_HandleSelectedResult} isNew={isNew} onNewToggle={Fn_HandleNewToggle} onCloseResults={() => setShowResults(false)} />)}

                {/* --- GST Input with onBlur handler and conditional error styling --- */}
                <Box sx={{ display: "flex", alignItems: "center", position: "relative", width: "95%", mt: 2 }}>
                    <FloatingLabelInput label="GST No" name="gstInNumber" type="text" value={gstInNumber} disabled={disableLeftSection} fullWidth onChange={(e) => setGstInNumber(e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, ""))} onBlur={handleGstBlur}
                        sx={
                            gstValid === false
                                ? {
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": { borderColor: "red" },
                                        "&:hover fieldset": { borderColor: "red" },
                                        "&.Mui-focused fieldset": { borderColor: "red" },
                                    },
                                    "& .MuiInputLabel-root": { color: "red" },
                                }
                                : {}
                        }
                    />
                    {gstLoading && (<CircularProgress size={16} sx={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", }} />)}
                    {!gstLoading && gstValid === true && (<CheckCircleIcon sx={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: "green", }} />)}
                    {!gstLoading && gstValid === false && (<CancelIcon sx={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: "red", }} />)}
                </Box>
                {gstError && (<Typography variant="caption" color="error" sx={{ mt: 0.5, width: "95%" }}> {gstError} </Typography>)}

                {/* Main Form */}
                <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }} onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {/* Left Column: Client and Address Details */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                <FloatingLabelInput label="Client Name" name="clientName" type="text" value={clientName} onChange={(e) => setClientName(e.target.value)} disabled={disableLeftSection} fullWidth required />
                                <FloatingLabelInput label="Mobile" name="mobile" type="text" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^\d]/g, ""))} disabled={disableLeftSection} fullWidth required />
                                <FloatingLabelInput label="Contact Person" name="contactPerson" type="text" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} disabled={disableLeftSection} fullWidth required />
                                <FloatingLabelInput label="Email" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={disableLeftSection} fullWidth />
                                <Box sx={{ width: "100%" }}>
                                    <TextArea label="Address" id="address" name="address" placeholder="Address" value={address} onChange={(e) => setAddress(e.target.value)} disabled={disableLeftSection} fullWidth />
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", zIndex: 10, widows: "100%" }}>
                                    <CitySearchInput value={city} flag={0} disabled={disableLeftSection} cities={cities} fullWidth onSelectCity={(cityObj) => { setCity(cityObj.city); setStateField(cityObj.state); setStateCode(cityObj.stateCode); }} />
                                    {stateField && (
                                        <Box sx={{ mt: 2, display: "flex", gap: 2, width: "100%" }}>
                                            <Box sx={{ width: "70%" }}>
                                                <FloatingLabelInput label="State" name="state" type="text" value={stateField} disabled readOnly fullWidth />
                                            </Box>
                                            <Box sx={{ width: "30%" }}>
                                                <FloatingLabelInput label="State Code" name="stateCode" type="text" value={stateCode} disabled readOnly fullWidth />
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Grid>

                        {/* Right Column: Additional Inquiry Details */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pl: { md: 2 } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0, width: "100%" }}>
                                    <Dropdown id="status" name="status" label="Status" valueKey="LeadStatusID" labelKey="LeadStatus" value={leadStatusID} options={statusOptions} onChange={handleStatusChange} disabled={false} />
                                    <Dropdown id="priority" name="priority" label="Priority" valueKey="id" labelKey="value" value={priorityID} options={priorityOptions} onChange={handlePriorityChange} disabled={false} />
                                </Box>
                                {overallMode === "followup" && leadStatusValue === "Failed" && (
                                    <Box sx={{ width: "100%" }}>
                                        <ReasonSearchDropdown options={reasonOptions} onSelect={(option) => setSelectedReason(option)} onAddNew={() => setShowAddNewReasonModal(true)} fullWidth
                                            onSearch={async (searchTerm) => {
                                                try {
                                                    const res = await axios.get(`${path}/api/reasons`, { params: { search: searchTerm } });
                                                    setReasonOptions(res.data.data || []);
                                                } catch (error) {
                                                    console.error("Error searching reasons:", error);
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                                <Box sx={{ width: "100%", py: 1 }}>
                                    <TextArea label="Requirement" id="requirement" name="requirement" placeholder="Requirement" value={requirement} onChange={(e) => setRequirement(e.target.value)} disabled={overallMode === "followup" && leadStatusValue === "Failed"} fullWidth required />
                                </Box>
                                <Box sx={{ width: "100%" }}>
                                    <TextArea label="Remarks" id="remarks" name="remarks" placeholder="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={overallMode === "followup" && leadStatusValue === "Failed"} fullWidth />
                                </Box>
                                <Box sx={{ display: "flex", width: "100%", pt: 2, alignItems: "center", gap: 2, mx: "auto" }}>
                                    {overallMode === "insert" ? (
                                        <DateInput label="Follow-Up Date" allowToday value={nextFollowUpDate} disabled={false} fullWidth
                                            onChange={(e) => {
                                                setNextFollowUpDate(e.target.value);
                                                setFollowupDateManuallySet(true);
                                            }}
                                            onTodaySelect={(todayValue) => {
                                                setNextFollowUpDate(todayValue);
                                                setFollowupDateManuallySet(true);
                                            }}
                                        />
                                    ) : (
                                        <DateInput label="Follow-Up Date" value={formatDateForInput(nextFollowUpDate)} disabled={false} onChange={(e) => setNextFollowUpDate(e.target.value)} fullWidth />
                                    )}
                                    <Dropdown id="assign" name="assign" label="Assign" valueKey="userId" labelKey="employeeName" value={userId} options={usersOptions} onChange={handleAssignChange} disabled={false} fullWidth />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    {formErrors.length > 0 && (
                        <Box sx={{ mx: "auto", maxWidth: 400, p: 2, bgcolor: "red.50", border: 1, borderColor: "red.500", borderRadius: 1, color: "red.700", mb: 4, }} >
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"> <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M12 5a7 7 0 110 14 7 7 0 010-14z" ></path> </svg>
                                <span style={{ fontWeight: "bold" }}>Please fix the following errors:</span>
                            </Box>
                            <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
                                {formErrors.map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                ))}
                            </ul>
                        </Box>
                    )}
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mt: 3, justifyContent: "center" }}>
                        <Button type="submit" disabled={isSubmitting || !gstValid || (overallMode === "followup" && leadStatusValue === "Failed")}> {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit"} </Button>
                        {overallMode !== "followup" && (<Button onClick={handleClose} variant="contained" color="error" disabled={isSubmitting}> Close </Button>)}
                    </Box>
                </Box>

                {showAddNewReasonModal && (<AddNewReasonModal open={showAddNewReasonModal} onClose={() => setShowAddNewReasonModal(false)} onSubmit={(newReasonRecord) => { setReasonOptions((prev) => [...prev, newReasonRecord]); setSelectedReason(newReasonRecord); }} />)}
            </Box>
        </FormContainer>
    );
}

export default InquiryForm_v1;